import React from 'react';
import { 
  Target, 
  Users, 
  Globe, 
  Shield, 
  Zap,
  ArrowRight,
  Download,
  Mail,
  Phone,
  MapPin,
  Rocket,
  ChevronRight,
  Star
} from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import ProductRoadmap from '../components/ProductRoadmap';

const Investors: React.FC = () => {
  const marketData = [
    { label: 'TAM', value: '$200B', description: 'India Logistics Market by 2030' },
    { label: 'SAM', value: '$45B', description: 'Addressable Fleet Management' },
    { label: 'SOM', value: '$2.5B', description: 'Serviceable Market (5-Year Target)' },
  ];

  const techHighlights = [
    { title: 'Cloud-Native Architecture', description: 'Built on AWS with 99.9% uptime SLA', icon: Zap },
    { title: 'Real-time Processing', description: 'Sub-second GPS tracking updates', icon: Target },
    { title: 'Enterprise Security', description: 'SOC 2 Type II, GDPR compliant', icon: Shield },
    { title: 'API-First Platform', description: '200+ REST endpoints, webhook support', icon: Globe },
  ];

  const businessModel = [
    { metric: 'Pricing Model', value: 'SaaS Subscription', detail: 'Per vehicle/month' },
    { metric: 'Avg. Contract Value', value: 'Rs.2.5L', detail: 'Annual recurring' },
    { metric: 'Gross Margin', value: '75%', detail: 'Highly scalable' },
    { metric: 'Customer LTV', value: 'Rs.15L', detail: '3.5 year retention' },
    { metric: 'CAC Payback', value: '8 months', detail: 'Efficient acquisition' },
    { metric: 'Net Revenue Retention', value: '125%', detail: 'Strong expansion' },
  ];

  const leadership = [
    {
      name: 'Jayaprasad (JP) Rao',
      role: 'Chief Executive Officer',
      bio: '20+ years in Banking and Digital Transformation. Building scalable, low-code platforms for financial operations and ventures.',
      education: 'Master’s in International Business from IIFT, New Delhi',
      image: '/team/Jayaprasad.png'
    },
    {
      name: 'Prasad Kande',
      role: 'Chief Operating Officer',
      bio: 'Former CEO of Telvi Technologies with 15+ years building fleet and logistics tech solutions. Leads GaadiMatic end-to-end operations — from fleet operator onboarding to customer success and delivery excellence.',
      education: '',
      image: '/team/prasadkande.png'
    },
    {
      name: 'Swamy Sirigada',
      role: 'Chief Financial Officer',
      bio: '18+ years in finance and mobility. Founder of Phoenitech Travels. Advocate for technology-led transformation in logistics.',
      education: '',
      image: '/team/swamy.png'
    },
    {
      name: 'Srinivasulu ',
      role: 'Chief Technology Officer',
      bio: '14+ years spanning Java, Python, PHP, React, and enterprise DevOps. Security-first engineering architect who builds high-availability systems that keep fleet operations running without downtime.',
      education: '',
      image: '/team/srinivasulu.png'
    }
  ];


  return (
    <div className="min-h-screen bg-white">
      <AnimatedBackground />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-gray-50">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-accent-orange/5" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6">
              <Rocket className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">Series A Fundraising</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-950 leading-tight mb-6">
              Building India's Fleet <br />
              <span className="text-primary-600">Operating System</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-10">
              GaadiMatic FleetX is transforming how 500+ fleet operators manage 10,000+ vehicles. 
              Join us in revolutionizing India's $200B logistics industry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:info@gaadimatic.com" 
                className="btn-primary group"
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Investor Relations
                <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
              </a>
              <button className="btn-secondary">
                <Download className="w-5 h-5 mr-2" />
                Download Pitch Deck
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Market Opportunity Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Massive Market Opportunity
            </h2>
            <p className="text-lg text-gray-600">
              India's logistics sector represents one of the largest untapped technology markets globally.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {marketData.map((item, index) => (
              <div key={index} className="relative">
                <div className="card p-8 text-center">
                  <div className="text-2xl font-extrabold text-primary-600 uppercase tracking-widest mb-2">
                    {item.label}
                  </div>
                  <div className="text-4xl sm:text-5xl font-bold text-navy-950 mb-3">
                    {item.value}
                  </div>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-6 h-6 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: '14.4%', label: 'of India GDP', sublabel: 'Logistics sector contribution' },
              { value: '10.5%', label: 'CAGR Growth', sublabel: '2019-2025 projected' },
              { value: '15-20%', label: 'Cost Reduction', sublabel: 'Through technology adoption' },
              { value: '$380B', label: 'By 2030', sublabel: 'Projected market size' },
            ].map((stat, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="text-3xl font-bold text-primary-600 mb-2">{stat.value}</div>
                <div className="font-semibold text-navy-950 mb-1">{stat.label}</div>
                <div className="text-sm text-gray-500">{stat.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-6">
                Technology and Product
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our platform is built on modern, scalable architecture designed to handle 
                millions of transactions daily. We have invested 3 years in building a 
                comprehensive solution that covers the entire fleet management lifecycle.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {techHighlights.map((tech, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <tech.icon className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-950 text-sm mb-1">{tech.title}</h4>
                      <p className="text-gray-600 text-xs">{tech.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-xl font-bold text-navy-950 mb-6">Competitive Moat</h3>
              <div className="space-y-4">
                {[
                  { title: 'India-Specific Features', desc: 'Fastag integration, local compliance, regional languages' },
                  { title: 'Unified Platform', desc: 'Only solution covering all 3 product lines in one system' },
                  { title: 'Network Effects', desc: 'Driver marketplace creating value for all participants' },
                  { title: 'Data Advantage', desc: '5 years of proprietary fleet operational data' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0">
                    <div className="w-6 h-6 bg-accent-orange/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Star className="w-3 h-3 text-accent-orange" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-950 text-sm">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Model Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Business Model
            </h2>
            <p className="text-lg text-gray-600">
              Scalable SaaS model with strong unit economics and multiple revenue streams.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {businessModel.map((item, index) => (
              <div key={index} className="card p-6 text-center">
                <div className="text-sm text-gray-500 mb-2">{item.metric}</div>
                <div className="text-3xl font-bold text-navy-950 mb-2">{item.value}</div>
                <div className="text-sm text-primary-600">{item.detail}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gray-50 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-navy-950 mb-6 text-center">Revenue Streams</h3>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { name: 'Subscription', percentage: '70%', color: 'bg-primary-600' },
                { name: 'Transaction Fees', percentage: '20%', color: 'bg-accent-orange' },
                { name: 'Premium Services', percentage: '10%', color: 'bg-green-500' },
              ].map((stream, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold text-navy-950 mb-2">{stream.percentage}</div>
                  <div className="text-gray-600 text-sm mb-3">{stream.name}</div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${stream.color} rounded-full`}
                      style={{ width: stream.percentage }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Leadership Team
            </h2>
            <p className="text-lg text-gray-600">
              Experienced operators with deep expertise in logistics and technology.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((person, index) => (
              <div key={index} className="card overflow-hidden group hover:shadow-xl transition-shadow duration-300">
                <div className="h-24 bg-gradient-to-br from-primary-600 to-primary-800" />
                <div className="px-6 pb-6">
                  <div className="relative -mt-12 mb-4">
                    <div className="w-24 h-24 bg-white rounded-full p-1 mx-auto overflow-hidden shadow-md group-hover:scale-105 transition-transform duration-300">
                      <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center overflow-hidden">
                        {person.image.startsWith('/') ? (
                          <img src={person.image} alt={person.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-2xl font-bold text-primary-700">{person.image}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-navy-950 mb-1">{person.name}</h3>
                    <p className="text-primary-600 font-medium mb-3">{person.role}</p>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-4">{person.bio}</p>
                    {person.education && (
                      <p className="text-gray-500 text-xs mt-2 italic">{person.education}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-sm">
              <Users className="w-5 h-5 text-primary-600" />
              <span className="text-gray-700">Backed by <span className="font-bold text-navy-950">20+</span> engineers and logistics experts</span>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Product Roadmap
            </h2>
            <p className="text-lg text-gray-600">
              Clear vision for expanding our platform capabilities and market reach.
            </p>
          </div>
          <ProductRoadmap />
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-padding bg-navy-950">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Invest in the Future of Fleet Management?
            </h2>
            <p className="text-lg text-gray-400 mb-10">
              Join us in building India's fleet operating system. 
              Contact our investor relations team for detailed financials and due diligence materials.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-6 mb-10">
              <a href="mailto:info@gaadimatic.com" className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Mail className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Email</div>
                <div className="text-gray-400 text-sm">info@gaadimatic.com</div>
              </a>
              <a href="tel:+919916500454" className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Phone className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Phone</div>
                <div className="text-gray-400 text-sm">+91 99165 00454</div>
              </a>
              <div className="bg-white/10 rounded-xl p-6 text-center">
                <MapPin className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Location</div>
                <div className="text-gray-400 text-sm">Hyderabad, India</div>
              </div>
            </div>

            <button className="btn-primary text-lg px-10">
              <Download className="w-5 h-5 mr-2" />
              Download Investor Deck
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Investors;
